import { Modal, TreeProps } from "antd";
import { DataNode } from "antd/lib/tree";
import { Role } from "models/Role";
import React from "react";
import { Model, ModelFilter } from "react3l-common";
import { roleRepository } from "repositories/role-repository";

export type TreeMenuType = DataNode & {
  isSelected: boolean;
  key: string;
  children?: TreeMenuType[];
};

export function usePermissionRoleTreeViewHook(
  role: Role,
  siteId: number,
  handleChangeAllField: (value: any) => void
) {
  const [firstInitTreeData, setFirstInitTreeData] =
    React.useState<boolean>(true);
  const treeDataRef = React.useRef<TreeMenuType[]>([]);
  const [menuFilter, setMenuFilter] = React.useState<ModelFilter>({
    ...new ModelFilter(),
  });
  const [checkedKeys, setCheckedKeys] = React.useState<string[]>([]);
  // List này chỉ lưu key cấp con, không lưu key cấp cha menu, vì BE trả cấp cha luôn là isSelected = true khi có quyền con
  const checkedKeysAllList = React.useRef<string[]>([]);
  const [checkedAll, setCheckedAll] = React.useState<boolean>(false);

  const handleConvertCheckedKeysToTreeData = React.useCallback(
    (checkedKeys) => {
      treeDataRef.current.forEach((menu, indexMenu) => {
        if (checkedKeys.includes(menu.key)) {
          treeDataRef.current[indexMenu].isSelected = true;
        } else {
          treeDataRef.current[indexMenu].isSelected = false;
        }
        menu?.children?.forEach((child: any, indexChild) => {
          if (checkedKeys.includes(child.key)) {
            treeDataRef.current[indexMenu].isSelected = true;
            treeDataRef.current[indexMenu].children[indexChild].isSelected =
              true;
          } else {
            treeDataRef.current[indexMenu].children[indexChild].isSelected =
              false;
          }
        });
      });
    },
    []
  );

  const handleSaveTree = React.useCallback(
    (checkedKeys: string[]) => {
      handleConvertCheckedKeysToTreeData(checkedKeys);
      handleChangeAllField &&
        handleChangeAllField({
          ...role,
          permissions: treeDataRef.current.map((item) => {
            return {
              ...item,
              siteId: siteId,
              actions: item?.children,
              children: [],
            };
          }),
        });
    },
    [handleChangeAllField, handleConvertCheckedKeysToTreeData, role, siteId]
  );

  const onCheck: TreeProps["onCheck"] = (newCheckedKeys: any, info) => {
    // Khi search, bỏ các key có trong checkedKeys mà không có list search (đảm bảo checkedKeys luôn lấy đúng các quyền đã check theo list search)
    if (menuFilter.name?.contain) {
      treeDataRef.current.forEach((menu, indexMenu) => {
        if (checkedKeys.includes(menu.key)) {
          checkedKeys.splice(checkedKeys.indexOf(menu.key), 1);
        }
        menu?.children?.forEach((child, indexChild) => {
          if (checkedKeys.includes(String(child.key))) {
            checkedKeys.splice(checkedKeys.indexOf(String(child.key)), 1);
          }
        });
        // Lấy các key mới trong list search, merge với checkedKeys
        setCheckedKeys([...checkedKeys, ...newCheckedKeys]);
        handleSaveTree([...checkedKeys, ...newCheckedKeys]);
      });
    } else {
      setCheckedKeys(newCheckedKeys);
      handleSaveTree(newCheckedKeys);
    }
  };

  const handleChangeInputSearch = React.useCallback(
    (value: string) => {
      setMenuFilter({ ...menuFilter, name: { contain: value } });
      treeDataRef.current = [];
      setTimeout(() => {
        setFirstInitTreeData(true);
      }, 1000);
    },
    [menuFilter]
  );

  const handleCheckedAll = React.useCallback(
    (e: any) => {
      const isChecked = e.target.checked;
      if (!isChecked) {
        Modal.confirm({
          title: "Bạn có chắc chắn muốn bỏ chọn tất cả các quyền không?",
          onOk: () => {
            setCheckedKeys([]);
            setCheckedAll(false);
            handleSaveTree([]);
          },
        });
      } else {
        setCheckedAll(true);
        setCheckedKeys(checkedKeysAllList.current);
        handleSaveTree(checkedKeysAllList.current);
      }
    },
    [handleSaveTree]
  );

  let countEffect = 0;

  React.useEffect(() => {
    firstInitTreeData &&
      role?.id &&
      siteId &&
      roleRepository
        .listMenuTree({
          ...menuFilter,
          roleId: { equal: role?.id },
          siteId: { equal: siteId },
        })
        .subscribe((result: Model[]) => {
          handleChangeAllField({
            ...role,
            permissions: result,
          });
          let countAll = 0;
          let checkedKeyListInit: string[] = [];
          if (result?.length > 0) {
            treeDataRef.current = result?.map((item) => {
              return {
                ...item,
                title: item?.name,
                key: `${item?.id}`,
                isSelected: item?.isSelected,
                children: item?.actions?.map((child: any) => {
                  countAll = countAll + 1;
                  checkedKeysAllList.current = [
                    ...checkedKeysAllList.current,
                    `${item?.id}-${child?.id}`,
                  ];
                  if (child?.isSelected)
                    checkedKeyListInit = [
                      ...checkedKeyListInit,
                      `${item?.id}-${child?.id}`,
                    ];
                  return {
                    ...child,
                    title: child?.name,
                    key: `${item?.id}-${child?.id}`,
                    isSelected: child?.isSelected,
                  };
                }),
              };
            });
            checkedKeysAllList.current = Array.from(
              new Set(checkedKeysAllList.current)
            );
            checkedKeys?.length === 0 &&
              setCheckedKeys(Array.from(new Set(checkedKeyListInit)));
            setFirstInitTreeData(false);
          }
        });
  }, [
    checkedKeys,
    countEffect,
    firstInitTreeData,
    handleChangeAllField,
    handleConvertCheckedKeysToTreeData,
    menuFilter,
    role,
    role?.id,
    siteId,
  ]);

  React.useEffect(() => {
    // Kiểm tra nếu tất cả các quyền con đều được tick -> Tick "Chọn tất cả"
    if (
      checkedKeys?.filter((key) => key.includes("-")).length ===
      checkedKeysAllList.current.length
    ) {
      setCheckedAll(true);
    } else {
      setCheckedAll(false);
    }
  }, [checkedKeys]);

  return {
    treeDataRef,
    menuFilter,
    onCheck,
    checkedKeys,
    handleChangeInputSearch,
    firstInitTreeData,
    checkedAll,
    handleCheckedAll,
  };
}
